import { useState } from "react";
import { Loader2, Copy, Check } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { createInvite } from "@/lib/admin/concierge-insights-api";

export function InviteByEmailDialog({
  open,
  onOpenChange,
  siteId,
  orgId,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  siteId: string;
  orgId: string;
}) {
  const [email, setEmail] = useState("");
  const [pending, setPending] = useState(false);
  const [link, setLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const reset = () => {
    setEmail("");
    setLink(null);
    setCopied(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) reset();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite by email</DialogTitle>
          <DialogDescription>
            Generates an invite link you can share. No account is created — the invited
            person completes their own signup.
          </DialogDescription>
        </DialogHeader>

        {!link ? (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="person@company.com"
              />
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <Label>Invite link</Label>
            <div className="flex gap-2">
              <Input value={link} readOnly />
              <Button
                variant="outline"
                onClick={async () => {
                  await navigator.clipboard.writeText(link);
                  setCopied(true);
                  toast({ title: "Copied" });
                }}
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        )}

        <DialogFooter>
          {!link ? (
            <Button
              disabled={!email.trim() || pending}
              onClick={async () => {
                setPending(true);
                try {
                  const { link: l } = await createInvite({
                    site_id: siteId,
                    org_id: orgId,
                    email,
                  });
                  setLink(l);
                } catch (e: any) {
                  toast({ title: "Failed", description: e.message, variant: "destructive" });
                } finally {
                  setPending(false);
                }
              }}
            >
              {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create invite"}
            </Button>
          ) : (
            <Button onClick={() => onOpenChange(false)}>Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default InviteByEmailDialog;
